import { createFileRoute } from "@tanstack/react-router";
import { IconUser } from "@offsilo/ui/icon";
import { ScrollRegion } from "@offsilo/ui/scroll";
import { SkeletonRows } from "@offsilo/ui/skeleton";
import { EmptyState } from "@offsilo/ui/state";
import { tourAnchor } from "@offsilo/ui/tour-anchor";
import { usePageChrome } from "@/app/chrome";
import { PageState } from "@/app/page-state";
import { BriefsPanel } from "@/features/briefs/BriefsPanel";
import { InventoryPanel } from "@/features/inventory/InventoryPanel";
import { OffboardingPanel } from "@/features/offboarding/OffboardingPanel";
import { ReferralsPanel } from "@/features/referrals/ReferralsPanel";
import { useCollection } from "@/data/hooks";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/_app/clients/$client/lifecycle")({ component: Page });

/** One client's lifecycle: referrals, briefs, the exit plan, and the website inventory. Found by the client's slug in the URL. */
function Page() {
  const { client: slug } = Route.useParams();
  const clients = useCollection("clients");
  const found = clients.data?.find((c) => c.slug === slug);
  usePageChrome([{ id: "clients", label: m.clientlife_crumb() }, { id: slug, label: found?.name ?? slug }]);

  return (
    <ScrollRegion name="clientlife" memoryKey={`clientlife:${slug}`} aria-label={m.clientlife_label()} gutter className="px-7 pb-6 max-tablet:px-4">
      <PageState
        query={clients}
        skeleton={<SkeletonRows rows={4} className="rounded-xl bg-card shadow-card" />}
        isEmpty={() => !found}
        empty={<EmptyState icon={IconUser} title={m.clientlife_missing_title()} body={m.clientlife_missing_body()} />}
      >
        {() =>
          found ? (
            <div className="flex max-w-4xl flex-col gap-12">
              <h1 {...tourAnchor("clients.lifecycle.header")} className="font-title text-3xl leading-tight">{found.name}</h1>
              <ReferralsPanel clientId={found.id} />
              <BriefsPanel clientId={found.id} />
              <OffboardingPanel clientId={found.id} />
              <InventoryPanel clientId={found.id} />
            </div>
          ) : null
        }
      </PageState>
    </ScrollRegion>
  );
}
